// First: `?lag=` must wrap the WebSocket before the Colyseus SDK loads (dev only).
import './net/dev-lag';
// Then the game data in the player's language, before any module reads it (ADR 0069).
import './i18n/localize-data';
import Phaser from 'phaser';
import type { SignInProvider } from '@shefira/shared';

import { playMusic, installAudio } from './audio/sound';
import { installUiSounds } from './audio/ui-sounds';
import { bindViewportResize, createGameConfig, registerScenes } from './game/config';
import { consumeResetLink, loadAccount, saveAccount } from './net/account';
import { clearSession, loadSession, type StoredSession } from './net/session';
import { consumeSignInReturn, PROVIDER_NAMES, setLinkNotice } from './net/sign-in';
import { installMouseButtons } from './settings/mouse-buttons';
import { installSettingsSync } from './settings/sync';
import { BootScene } from './scenes/boot-scene';
import { installNativeShell } from './platform/native';
import { consumeHomeRequest } from './ui/home';
import { installOverflowAudit } from './ui/overflow-audit';
import { hideLoader, showLoader } from './ui/loader';
import { showRoster } from './ui/roster';
import { showFrontSoundToggle } from './ui/sound-toggle';
import { installTouchBasics } from './ui/touch-basics';
import { showWelcome } from './ui/welcome';

/**
 * A session first, then the game. Three ways in, in order of how little they
 * ask of the player: a character session left by a previous visit, an account
 * session that only needs a character picked, or the welcome screen.
 *
 * Coming home from the game is the exception: the character session is still
 * there, but the player asked for the roster, so it opens first — from the
 * account token when there is one, from the character's own otherwise.
 */
async function resolveSession(): Promise<StoredSession> {
  let existing = loadSession();
  let account = loadAccount();

  // Back from Google or Steam (ADR 0054): a new account session, or a provider linked from the game.
  let welcomeError: string | undefined;
  const back = await consumeSignInReturn(existing?.token ?? account?.accountToken);
  if (back.kind === 'done') {
    const { result } = back;
    if (result.kind === 'session') {
      saveAccount(result.session);
      clearSession();
      existing = null;
      account = result.session;
    } else {
      const provider: SignInProvider = result.provider;
      setLinkNotice({ text: `${PROVIDER_NAMES[provider]} est lié à ton compte.`, tone: 'ok' });
    }
  } else if (back.kind === 'failed') {
    if (back.linking) setLinkNotice({ text: back.message, tone: 'refused' });
    else welcomeError = back.message;
  }

  // Opened from a mailed reset link (ADR 0056): that face first, whatever session this browser holds.
  const resetToken = consumeResetLink();
  if (resetToken !== null) {
    hideLoader();
    const reset = await showWelcome({ resetToken });
    if (reset.kind === 'character') return reset.session;
    clearSession();
    return showRoster({ token: reset.session.accountToken });
  }

  if (consumeHomeRequest()) {
    const token = account?.accountToken ?? existing?.token;
    if (token !== undefined) {
      hideLoader();
      return showRoster({ token, currentId: existing?.character.id });
    }
  }
  if (existing !== null) return existing;
  // A screen to fill in: the loading screen makes way for it.
  hideLoader();
  if (account !== null) return showRoster({ token: account.accountToken });

  const welcome = await showWelcome(
    welcomeError !== undefined ? { signInError: welcomeError } : {},
  );
  return welcome.kind === 'character'
    ? welcome.session
    : showRoster({ token: welcome.session.accountToken });
}

async function start(): Promise<void> {
  // The layout check of the interface, from the console (`shefiraOverflow()`).
  if (import.meta.env.DEV) installOverflowAudit();
  // The iOS and Android shells (ADR 0075); nothing in a browser.
  installNativeShell();
  installAudio();
  // Mutable from the very first screen, before the title theme starts.
  showFrontSoundToggle();
  installUiSounds();
  // Long presses, the on-screen keyboard (ADR 0043); inert with a mouse.
  installTouchBasics();
  installMouseButtons();
  // The title theme, from the first click; the world swaps it for its place's.
  playMusic('music.title');
  const session = await resolveSession();
  // Back up (if the welcome screen or the roster lifted it) while the world loads.
  showLoader();
  // The account's settings (ADR 0029), from here on kept in step with the server.
  installSettingsSync(session.token);
  const game = new Phaser.Game(createGameConfig('game'));
  registerScenes(game);
  bindViewportResize(game);
  game.scene.start(BootScene.KEY, { session });
}

void start();
